import { PlanPromoRule } from '@if/core/types'
import { serializeParameter } from '@if/core/utils'
import { useSelectedCompany } from '@src/contexts/SelectedCompanyProvider'
import { BannerDto } from '@src/data/types/BannerSchema'
import { MerchantFilter } from '@src/data/types/Filter'
import { MerchantDashboardUser } from '@src/data/types/MerchantDashboardUserSchema'
import { MerchantFinancingProgramConfig } from '@src/data/types/MerchantFinancingProgramConfig'
import { MerchantPaymentPlan } from '@src/data/types/MerchantPaymentPlan'
import { PaymentPlanDto } from '@src/data/types/PaymentPlanDto'
import { PaymentPlanFiltersDto } from '@src/data/types/PaymentPlanFiltersDto'
import { UpdateParentDto } from '@src/data/types/UpdateParentDto'
import { reportErrorToConsole } from '@src/services/error-logger'
import {
  keepPreviousData,
  MutationFunction,
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useCallback } from 'react'
import { Merchant, RateAgreement } from '../../types/Merchant'
import { getApiClient } from '../api-client'
import { downloadDocumentFromAPI, downloadDocumentFromAzureStorage } from './files-api'
import { transformPaymentPlanFromApi } from './merchants-api-transform'

const SCOPEMERCHANTS = 'merchants'
const SCOPEBANNERS = 'banners'
const SCOPETERRITORIES = 'territories'
const SCOPEREPORTS = 'reports'
const SCOPEPAYMENTPLANS = 'paymentPlans'
const LIST = 'list'
const DETAIL = 'detail'
const USERS = 'users'
const PAYMENTPLAN = 'paymentPlan'
const PROMOPLAN = 'promoPlan'
const MERCHANTSBYPAYMENTPLAN = 'merchantsByPaymentPlan'
const CUSTOMBUYDOWNTERMRATES = 'customBuydownTermRates'

const fiveSeconds = 5000

export const keysFactory = {
  allMerchants: () => [{ scope: SCOPEMERCHANTS }] as const,
  allMerchantsLists: () => [{ scope: SCOPEMERCHANTS, entity: LIST }] as const,
  merchantsList: (filters: MerchantFilter) => [{ scope: SCOPEMERCHANTS, entity: LIST, ...filters }] as const,
  allMerchantsDetails: () => [{ scope: SCOPEMERCHANTS, entity: DETAIL }] as const,
  merchantDetail: (id: string) => [{ scope: SCOPEMERCHANTS, entity: DETAIL, id }] as const,
  allMerchantsUsers: () => [{ scope: SCOPEMERCHANTS, entity: USERS }] as const,
  merchantUsers: (merchantId: string) => [{ scope: SCOPEMERCHANTS, entity: USERS, merchantId }] as const,
  allBanners: () => [{ scope: SCOPEBANNERS }] as const,
  allBannersLists: () => [{ scope: SCOPEBANNERS, entity: LIST }] as const,
  bannersList: (id: string) => [{ scope: SCOPEBANNERS, entity: LIST, id }] as const,
  allBannersDetails: () => [{ scope: SCOPEBANNERS, entity: DETAIL }] as const,
  bannerDetails: (id: string) => [{ scope: SCOPEBANNERS, entity: DETAIL, id }] as const,
  bannerMerchants: (id: string) => [{ scope: SCOPEBANNERS, entity: SCOPEMERCHANTS, id }] as const,
  territoryMerchants: (id: string) => [{ scope: SCOPETERRITORIES, entity: SCOPEMERCHANTS, id }] as const,
  merchantPaymentPlans: (id: string) => [{ scope: SCOPEMERCHANTS, entity: PAYMENTPLAN, id }] as const,
  allMerchantPromoPlans: () => [{ scope: SCOPEMERCHANTS, entity: PROMOPLAN }] as const,
  merchantPromoPlans: (id: string) => [{ scope: SCOPEMERCHANTS, entity: PROMOPLAN, id }] as const,
  allCustomBuydownTermRates: (id: string) =>
    [{ scope: SCOPEMERCHANTS, entity: CUSTOMBUYDOWNTERMRATES, id }] as const,
  customBuydownTermRates: (
    id: string,
    merchantFeeRate: number,
    borrowerFeeRate: number,
    maxFinancingAmount: number | null,
  ) =>
    [
      {
        scope: SCOPEMERCHANTS,
        entity: CUSTOMBUYDOWNTERMRATES,
        id,
        merchantFeeRate,
        borrowerFeeRate,
        maxFinancingAmount,
      },
    ] as const,
  merchantsByPaymentPlan: (id: string) => [{ scope: SCOPEMERCHANTS, entity: MERCHANTSBYPAYMENTPLAN, id }] as const,
  merchantReports: (id: string) => [{ scope: SCOPEREPORTS, entity: LIST, id }] as const,
  allTimeReportStatus: (id: string) => [{ scope: SCOPEREPORTS, entity: 'allTime', id }] as const,

  searchPaymentPlans: (filters: PaymentPlanFiltersDto) => [{ scope: SCOPEPAYMENTPLANS, ...filters }] as const,
}

const getMerchantList = async ({
  queryKey: [filters],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['merchantsList']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<Merchant[]>('/Merchants', {
    params: filters,
    paramsSerializer(params) {
      return serializeParameter(params)
    },
  })
  return response.data
}

export function useMerchantsList(filter: MerchantFilter): [Merchant[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: [...keysFactory.merchantsList(filter)],
    queryFn: getMerchantList,
    initialData: [],
  })

  return [data, isFetching]
}

const getMerchant = async ({
  queryKey: [{ id }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['merchantDetail']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<Merchant>(`/Merchants/${encodeURIComponent(id)}`)
  return response.data
}

const sixtySeconds = 60_000

export function useMerchantById(id: string): [Merchant | null, boolean] {
  const { data, isLoading } = useQuery({
    queryKey: keysFactory.merchantDetail(id),
    queryFn: getMerchant,
    enabled: !!id,
    refetchOnWindowFocus: true,
    refetchInterval: sixtySeconds,
  })

  return [data ?? null, isLoading]
}

const updateMerchantParent: MutationFunction<Merchant, UpdateParentDto> = async (dto: UpdateParentDto) => {
  const apiClient = getApiClient()
  const response = await apiClient.put(`/Merchants/${dto.merchantId}/Parent`, dto)
  return response.data as Merchant
}

export function useUpdateMerchantParent(): [MutationFunction<Merchant, UpdateParentDto>, boolean, () => void] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, reset } = useMutation({
    mutationFn: updateMerchantParent,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: keysFactory.allMerchantsLists(),
      })
    },
  })

  return [mutateAsync, isPending, reset]
}

const getMerchantUsers = async ({
  queryKey: [{ merchantId }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['merchantUsers']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get(`/Merchants/${merchantId}/Users`)
  return response.data as MerchantDashboardUser[]
}

export function useMerchantUserList(merchantId: string): [MerchantDashboardUser[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.merchantUsers(merchantId),
    queryFn: getMerchantUsers,
    initialData: [],
  })

  return [data, isFetching]
}

const createBanner: MutationFunction<Merchant, BannerDto> = async (dto: BannerDto) => {
  const apiClient = getApiClient()
  const response = await apiClient.post('/Banners', dto)
  return response.data as Merchant
}

export function useCreateBanner(): [MutationFunction<Merchant, BannerDto>, boolean, () => void] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, reset } = useMutation({
    mutationFn: createBanner,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: keysFactory.allMerchantsLists(),
      })
    },
  })

  return [mutateAsync, isPending, reset]
}

const updateBanner: MutationFunction<Merchant, BannerDto> = async (dto: BannerDto) => {
  const apiClient = getApiClient()
  const response = await apiClient.put(`/Banners/${dto.id}`, dto)
  return response.data as Merchant
}

export function useUpdateBanner(): [MutationFunction<Merchant, BannerDto>, boolean, () => void, Error | null] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, reset, error } = useMutation({
    mutationFn: updateBanner,
    onSuccess: async (data) => {
      const cached = queryClient.getQueryData<Merchant>(keysFactory.bannerDetails(data.id))
      const rebateChanged = (cached?.flashPlanRateRebate ?? 0) !== (data.flashPlanRateRebate ?? 0)

      queryClient.setQueryData(keysFactory.bannerDetails(data.id), data)
      await queryClient.invalidateQueries({
        queryKey: keysFactory.allMerchantsDetails(),
      })

      // A banner rebate change reprices the banner's own plan and re-copies it to every child, so all
      // cached plans are stale — not just this banner's.
      if (rebateChanged) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: keysFactory.allMerchantPromoPlans() }),
          queryClient.invalidateQueries({ queryKey: keysFactory.allCustomBuydownTermRates(data.id) }),
        ])
      }
    },
  })

  return [mutateAsync, isPending, reset, error]
}

const getMerchantsByParentId = async ({
  queryKey: [{ id }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['bannerMerchants']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.post(`/Banners/${id}/Subsidiaries`)
  return response.data as Merchant[]
}

export function useMerchantByParentId(id: string): [Merchant[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.bannerMerchants(id),
    queryFn: getMerchantsByParentId,
    enabled: !!id,
    initialData: [],
  })

  return [data, isFetching]
}

const getMerchantsByTerritoryId = async ({
  queryKey: [{ id }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['territoryMerchants']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get(`/Territories/${id}/Merchants`)
  return response.data as Merchant[]
}

export function useMerchantsByTerritoryId(id: string): [Merchant[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.territoryMerchants(id),
    queryFn: getMerchantsByTerritoryId,
    initialData: [],
  })

  return [data, isFetching]
}

interface DeleteUserParams {
  email: string
  merchantId: string
}

const deleteUser = async ({ email }: DeleteUserParams) => {
  const apiClient = getApiClient()
  await apiClient.delete(`/Users?email=${encodeURIComponent(email)}`)
}

export function useDeleteUser(): [MutationFunction<void, DeleteUserParams>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: deleteUser,
    onSuccess: (_, variables) => {
      const { email, merchantId } = variables
      const previousUsers = queryClient.getQueryData<MerchantDashboardUser[]>(keysFactory.merchantUsers(merchantId))

      if (previousUsers) {
        const updatedUsers = previousUsers.filter((user) => user.email !== email)
        queryClient.setQueryData(keysFactory.merchantUsers(merchantId), updatedUsers)
      }
    },
  })

  return [mutateAsync, isPending]
}

export interface UpdateMerchantDto {
  territoryId: string | null
  noPaymentPolicy: boolean
  hideAddress: boolean
  canSkipFlinks: boolean
  canPayBorrowers: boolean
  canAcceptHighEstimate: boolean
  showInterestRateOnMerchantDashboard: boolean
  automaticIncomeApproval: boolean
  planPromoRules: PlanPromoRule[]
  merchantId: string
  tags: string[] | null
  reportGenerationFrequency: string
  reportRecipientsEmailList: string[]
  rateAgreements: RateAgreement[] | null
  financingProgramConfigs: MerchantFinancingProgramConfig[]
  // Promotion mode: true = "Custom promotion" (flash plans defined in ManageMerchant),
  // false = "Preconfigured promotions". Omit/null to leave the stored value unchanged.
  flashPlanDefinedInManageMerchant?: boolean | null
  // Custom promotion notification recipients. Omit/null to leave the stored value unchanged.
  promoNotificationEmailList?: string[] | null
  // Custom Buydown rate rebate (single source of truth). Omit/null to leave the stored value unchanged.
  flashPlanRateRebate?: number | null
}

const updateMerchant: MutationFunction<Merchant, UpdateMerchantDto> = async (dto: UpdateMerchantDto) => {
  const apiClient = getApiClient()
  const response = await apiClient.put(`/Merchants/${dto.merchantId}`, dto)
  return response.data as Merchant
}

export function useUpdateMerchant(): [
  MutationFunction<Merchant, UpdateMerchantDto>,
  boolean,
  AxiosError<string> | null,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, reset, error } = useMutation({
    mutationFn: updateMerchant,
    onSuccess: async (updatedMerchant: Merchant) => {
      const cached = queryClient.getQueryData<Merchant>(keysFactory.merchantDetail(updatedMerchant.id))
      const rebateChanged = (cached?.flashPlanRateRebate ?? 0) !== (updatedMerchant.flashPlanRateRebate ?? 0)

      queryClient.setQueryData(keysFactory.merchantDetail(updatedMerchant.id), (oldMerchant: Merchant | undefined) => {
        if (!oldMerchant) return updatedMerchant

        return {
          ...oldMerchant,
          noPaymentPolicy: updatedMerchant.noPaymentPolicy,
          hideAddress: updatedMerchant.hideAddress,
          canSkipFlinks: updatedMerchant.canSkipFlinks,
          canPayBorrowers: updatedMerchant.canPayBorrowers,
          automaticIncomeApproval: updatedMerchant.automaticIncomeApproval,
          showInterestRateOnMerchantDashboard: updatedMerchant.showInterestRateOnMerchantDashboard,
          territoryId: updatedMerchant.territoryId,
          tags: updatedMerchant.tags,
          flashPlanRateRebate: updatedMerchant.flashPlanRateRebate,
          flashPlanDefinedInManageMerchant: updatedMerchant.flashPlanDefinedInManageMerchant,
          promoNotificationEmailList: updatedMerchant.promoNotificationEmailList,
        }
      })

      // The server bakes the rebate into the plan's rates and reprices on change, so both the saved plan
      // and the preview grid are stale until refetched.
      if (rebateChanged) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: keysFactory.merchantPromoPlans(updatedMerchant.id) }),
          queryClient.invalidateQueries({ queryKey: keysFactory.allCustomBuydownTermRates(updatedMerchant.id) }),
        ])
      }
    },
  })

  return [mutateAsync, isPending, error as AxiosError<string>, reset]
}

const getMerchantPaymentPlans = async ({
  queryKey: [{ id }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['merchantPaymentPlans']>>) => {
  if (!id) {
    return []
  }

  const apiClient = getApiClient()
  const response = await apiClient.get<MerchantPaymentPlan[]>(`/Merchants/${encodeURIComponent(id)}/PaymentPlans`)
  return response.data
}

export function useGetMerchantPaymentPlans(id: string): [MerchantPaymentPlan[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.merchantPaymentPlans(id),
    queryFn: getMerchantPaymentPlans,
    initialData: [],
    enabled: !!id,
  })
  return [data, isFetching]
}


export interface MerchantReportSummaryDto {
  fileName: string
  createdOn: Date
  sasTokenUrl: string
}

const getMerchantReports = async ({
  queryKey: [{ id }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['merchantReports']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<MerchantReportSummaryDto[]>(`/Merchants/Reports/${encodeURIComponent(id)}`)
  return response.data
}

export function useGetMerchantReports(id: string, shouldPoll: boolean): [MerchantReportSummaryDto[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.merchantReports(id),
    queryFn: getMerchantReports,
    refetchInterval: shouldPoll ? fiveSeconds : undefined,
  })
  return [data ?? [], isFetching]
}

const forceGenerateReport: MutationFunction<void, string> = async (merchantId: string) => {
  const apiClient = getApiClient()
  await apiClient.post(`/Merchants/Reports/${encodeURIComponent(merchantId)}/ForceSendMerchantReport`)
}

export function useForceGenerateReport(merchantId: string): [() => Promise<void>, boolean, () => void] {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending, reset } = useMutation({
    mutationFn: () => forceGenerateReport(merchantId),
    onSuccess: () => {
      // Invalidate merchant reports list to fetch latest
      queryClient
        .invalidateQueries({
          queryKey: keysFactory.merchantReports(merchantId),
        })
        .catch(reportErrorToConsole)
    },
  })
  return [mutateAsync, isPending, reset]
}

export type AllTimeReportStatus = 'neverGenerated' | 'generating' | 'ready'

export interface AllTimeReportStatusResultDto {
  status: AllTimeReportStatus
  sasTokenUrl: string | null
  generatedOn: Date | null
  fileName: string | null
}

const generateAllTimeReport = async (merchantId: string): Promise<void> => {
  const apiClient = getApiClient()
  await apiClient.post(`/Merchants/Reports/${encodeURIComponent(merchantId)}/AllTimeReport/Refresh`)
}

export function useGenerateAllTimeReport(merchantId: string): [() => Promise<void>, boolean, () => void] {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending, reset } = useMutation({
    mutationFn: () => generateAllTimeReport(merchantId),
    onSuccess: () => {
      queryClient.setQueryData(keysFactory.allTimeReportStatus(merchantId), {
        status: 'generating' as const,
        sasTokenUrl: null,
        generatedOn: null,
        fileName: null,
      })
    },
  })
  return [mutateAsync, isPending, reset]
}

const getAllTimeReportStatus = async ({
  queryKey: [{ id }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['allTimeReportStatus']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<AllTimeReportStatusResultDto>(
    `/Merchants/Reports/${encodeURIComponent(id)}/AllTimeReport/Status`,
  )
  return response.data
}

export function useGetAllTimeReportStatus(merchantId: string): [AllTimeReportStatusResultDto | undefined, boolean] {
  const queryClient = useQueryClient()
  const cachedData = queryClient.getQueryData<AllTimeReportStatusResultDto>(keysFactory.allTimeReportStatus(merchantId))

  const { data, isFetching } = useQuery({
    queryKey: keysFactory.allTimeReportStatus(merchantId),
    queryFn: getAllTimeReportStatus,
    refetchInterval: (query) => (query.state.data?.status === 'generating' ? fiveSeconds : false),
    enabled: cachedData === undefined || cachedData?.status === 'generating',
  })

  return [data, isFetching]
}

export function useInvalidateMerchantReports(merchantId: string): () => void {
  const queryClient = useQueryClient()
  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: keysFactory.merchantReports(merchantId) }).catch(reportErrorToConsole)
  }, [queryClient, merchantId])
}

interface DownloadFromAzureParams {
  fileName: string
  sasTokenUrl: string
}

export function useDownloadDocumentFromAzureStorage(): [
  MutationFunction<void, DownloadFromAzureParams>,
  boolean,
  boolean,
  () => void,
] {
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: ({ fileName, sasTokenUrl }: DownloadFromAzureParams) =>
      downloadDocumentFromAzureStorage(fileName, sasTokenUrl),
  })
  return [mutateAsync, isPending, isError, reset]
}

export function useDownloadMerchantsListReport(): [MutationFunction<void, void>, boolean, boolean, () => void] {
  const selectedCompany = useSelectedCompany()

  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: () => {
      const path = `/Merchants/Reports/${selectedCompany}/MerchantsList`
      const filename = `merchant-list-${new Date().toISOString().split('T')[0]}-${selectedCompany}.csv`

      return downloadDocumentFromAPI(filename, path)
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

export interface PromoTermRateDto {
  termMonths: number
  promoRate: number
  promoDurationMonths?: number | null
  regularRate: number
  // Maximum eligible financing amount; null = no maximum (unlimited).
  maxFinancingAmount: number | null
}

export interface PromoPlanContentDto {
  family: string
  label: string
  merchantFeeRate: number
  borrowerFeeRate: number
  maxFinancingAmount: number | null
  activeFrom: string
  expiresOn?: string | null
  supportsChargeback: boolean
  promoCodePrefix?: string | null
  paymentPlanId?: string | null
  // VersionTag of the head the client read; required on PUT so the server can reject a stale-version save.
  versionTag?: string | null
}

export interface PromoPlanDto extends PromoPlanContentDto {
  id: string
  merchantId: string
  planKey: string
  source: string
  // Computed server-side (BuyDownRateCalculator) — never sent by the client. Already net of appliedRebate.
  promoTermRates: PromoTermRateDto[]
  appliedRebate: number
  createdByUserFullname: string
  createdOn: string
  updatedByUserFullname: string
  updatedOn: string
  updatedBySurface: string
  versionTag: string | null
}

const getCurrentPromoPlans = async ({
  queryKey: [{ id }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['merchantPromoPlans']>>): Promise<PromoPlanDto[]> => {
  if (!id) return []
  const apiClient = getApiClient()
  const response = await apiClient.get<PromoPlanDto[]>(`/Merchants/${encodeURIComponent(id)}/PromoPlans`)
  return response.data
}

export function useGetCurrentPromoPlans(merchantId: string): [PromoPlanDto[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.merchantPromoPlans(merchantId),
    queryFn: getCurrentPromoPlans,
    initialData: [],
    enabled: !!merchantId,
  })
  return [data, isFetching]
}

// Mirrors the server-side calculation the Save path freezes onto the plan; terms filtered out
// (e.g., by the max-amount cap) are omitted.
const getCustomBuydownTermRates = async ({
  queryKey: [{ id, merchantFeeRate, borrowerFeeRate, maxFinancingAmount }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['customBuydownTermRates']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<PromoTermRateDto[]>(
    `/Merchants/${encodeURIComponent(id)}/PromoPlans/CustomBuydown/TermRates`,
    { params: { merchantFeeRate, borrowerFeeRate, maxFinancingAmount } },
  )
  return response.data
}

// Returns the customer-facing rates, already net of the merchant's rebate, which the server reads itself.
export function useGetCustomBuydownTermRates(
  merchantId: string,
  merchantFeeRate: number,
  borrowerFeeRate: number,
  maxFinancingAmount: number | null,
  enabled: boolean,
): [PromoTermRateDto[] | undefined, boolean, Error | null] {
  const { data, isLoading, error } = useQuery({
    queryKey: keysFactory.customBuydownTermRates(merchantId, merchantFeeRate, borrowerFeeRate, maxFinancingAmount),
    queryFn: getCustomBuydownTermRates,
    enabled: enabled && !!merchantId,
    // Keep showing the previous grid while a new one is fetched, so live typing doesn't flash.
    placeholderData: keepPreviousData,
  })
  return [data, isLoading, error]
}

interface UpsertCustomBuydownParams {
  merchantId: string
  // When set, appends a revision to the existing plan (PUT); otherwise mints a new one (POST).
  planKey?: string | null
  content: PromoPlanContentDto
}

const upsertCustomBuydown: MutationFunction<PromoPlanDto, UpsertCustomBuydownParams> = async ({
  merchantId,
  planKey,
  content,
}: UpsertCustomBuydownParams) => {
  const apiClient = getApiClient()
  const response = planKey
    ? await apiClient.put(`/Merchants/${merchantId}/PromoPlans/${encodeURIComponent(planKey)}`, content)
    : await apiClient.post(`/Merchants/${merchantId}/PromoPlans`, content)
  return response.data as PromoPlanDto
}

export function useUpsertCustomBuydown(): [
  MutationFunction<PromoPlanDto, UpsertCustomBuydownParams>,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, reset } = useMutation({
    mutationFn: upsertCustomBuydown,
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: keysFactory.merchantPromoPlans(variables.merchantId) }),
        queryClient.invalidateQueries({ queryKey: keysFactory.merchantDetail(variables.merchantId) }),
      ])
    },
  })

  return [mutateAsync, isPending, reset]
}

const searchPaymentPlans = async ({
  queryKey: [filters],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['searchPaymentPlans']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.post<PaymentPlanDto[]>('/PaymentPlans/Search', filters)
  return response.data.map(transformPaymentPlanFromApi)
}

export function useSearchPaymentPlans(filters: PaymentPlanFiltersDto): [PaymentPlanDto[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: [...keysFactory.searchPaymentPlans(filters)],
    queryFn: searchPaymentPlans,
    initialData: [],
  })

  return [data, isFetching]
}

const getPaymentPlanById = async ({
  queryKey: [{ id }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['merchantPaymentPlans']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<PaymentPlanDto>(`/PaymentPlans/${encodeURIComponent(id)}`)
  return response.data
}

export function useGetPaymentPlanById(id: string): [PaymentPlanDto | null, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: [...keysFactory.merchantPaymentPlans(id)],
    queryFn: getPaymentPlanById,
    enabled: !!id,
  })
  return [data ?? null, isFetching]
}

const getMerchantsByPaymentPlanId = async ({
  queryKey: [{ id }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['merchantsByPaymentPlan']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<Merchant[]>(`/PaymentPlans/${encodeURIComponent(id)}/merchants`)
  return response.data
}

export function useGetMerchantsByPaymentPlanId(paymentPlanId: string): [Merchant[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: [...keysFactory.merchantsByPaymentPlan(paymentPlanId)],
    queryFn: getMerchantsByPaymentPlanId,
    enabled: !!paymentPlanId,
    initialData: [],
  })
  return [data, isFetching]
}
